import Select from '@/components/dataTable/TableSelect';
import { Tabs } from '@/components/tabs/Tabs';
import { Input } from '@/design-system/input';
import { useTopStoreList } from '@/features/dashboard/conversionAnalytics/hooks/useConversionAnalytics';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { TopStore } from '@/services/dashboard/conversionAnalytics/conversionAnalytics.types';
import { ChevronRight, Search } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';

const RANKING_TABS = [
  { label: 'Top 10', value: 'top-10', topCount: 10 },
  { label: 'Top 50', value: 'top-50', topCount: 50 },
  { label: 'Top 100', value: 'top-100', topCount: 100 },
] as const;

export default function SelectStoreModal({
  initialSelected,
  onSelect,
}: {
  initialSelected: string;
  onSelect: (clubId: string, storeName: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [location, setLocation] = useState('');
  const { t } = useDynamicLocale();
  const [activeRankingTab, setActiveRankingTab] = useQueryState('rankingTab', parseAsString.withDefault('top-10'));
  const [selected, setSelected] = useState(initialSelected);
  const { data: locationOptions = [] } = useDropdown('location');
  const updateDebouncedSearch = useDebouncedCallback((value: string) => setDebouncedSearch(value), 300);
  const topCount = RANKING_TABS.find(({ value }) => value === activeRankingTab)?.topCount ?? 10;
  const { topStores, isLoading } = useTopStoreList({
    location: location || undefined,
    search: debouncedSearch || undefined,
    topCount,
    fromDate: 0,
    toDate: 0,
  });

  const handleSelect = (store: TopStore) => {
    setSelected(store.clubId);
    onSelect(store.clubId, store.clubName);
  };

  const onTabChange = (tab: string): void => {
    void setActiveRankingTab(tab);
  };

  const tabItems = RANKING_TABS.map(({ label, value }) => ({
    label,
    value,
    content: <RankingPanel label={label} stores={topStores} isLoading={isLoading} />,
  }));

  function StoreListItem({ store }: { store: TopStore }) {
    const isSelected = store.clubId === selected;
    const rank = Number(store.rankNo);
    const isTopThree = rank >= 1 && rank <= 3;

    return (
      <button
        type="button"
        onClick={() => handleSelect(store)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-2 py-3 text-left hover:bg-surface-raised cursor-pointer',
          isSelected && 'bg-surface-track hover:bg-surface-track',
        )}
      >
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold text-on-color',
              isTopThree ? 'bg-sidebar' : 'bg-secondary',
            )}
          >
            #{store.rankNo}
          </span>
          <div>
            <p className="whitespace-pre text-sm font-medium text-foreground-heading">
              {store.clubName}
              {'  '}
              {store.locationName}
            </p>
            <p className="text-xs text-foreground-caption">Club ID: {store.clubCode}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-sm font-medium text-foreground-caption">
          <span>{store.tapCount}タップ</span>
          <ChevronRight className="h-4 w-4" />
        </div>
      </button>
    );
  }

  function RankingPanel({ label, stores, isLoading }: { label: string; stores: TopStore[]; isLoading: boolean }) {
    return (
      <div className="mt-4 flex flex-col gap-1">
        <div className="flex items-center gap-2.5">
          <p className="text-sm font-semibold text-foreground-heading">{label}（タップ数順）</p>
          <div className="h-[2px] flex-1 bg-border-divider" />
        </div>
        <div className="relative flex h-[215px] flex-col divide-y divide-border-soft overflow-y-auto scrollbar-thin">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-foreground-caption">Loading...</div>
          ) : stores.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-foreground-caption">
              {t('common.page.table.noResult')}
            </div>
          ) : (
            stores.map((store) => <StoreListItem key={store.clubId} store={store} />)
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full px-6 pt-4 pb-8">
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-soft" />
          <Input
            value={search}
            onChange={(event) => {
              const value = event.target.value;
              setSearch(value);
              updateDebouncedSearch(value);
            }}
            placeholder={t('dashboard.conversion.modal.searchByStoreName')}
            className="border-surface-hover pl-9"
          />
        </div>
        <Select
          value={location}
          hideDefaultOption
          onValueChange={setLocation}
          options={[{ label: t('dashboard.conversion.modal.selectAnArea'), value: 'default' }, ...locationOptions]}
        />
      </div>

      <Tabs
        listClassName="bg-card"
        triggerClassName="bg-card rounded-lg py-0 px-2 h-7 text-sm "
        defaultActive={activeRankingTab}
        items={tabItems}
        onChange={onTabChange}
      />
    </div>
  );
}
